/**
 * PostModeration — Admin moderation controls for a single post.
 *
 * Renders the admin action tray (pin / unpin, edit, remove) for a feed post.
 * Only visible when the current user is an admin.
 *
 * Design source: design/project/CitiDev Hub.html — admin affordances on post cards.
 * Design system: 4px grid, semantic colour tokens, full interactive-state coverage,
 * dark mode — per design/project/uploads/AI_LAB_DESIGN_PRINCIPLES.md.
 *
 * [STUB] This component is a scaffold stub.  A future UI story must:
 *   1. Connect to the real admin API (PATCH /api/v1/admin/posts/:id/pin,
 *      PATCH /api/v1/admin/posts/:id/edit, DELETE /api/v1/admin/posts/:id).
 *   2. Implement the edit modal / inline edit form.
 *   3. Hook into the feed store so pinned / removed state propagates immediately.
 *   4. Enforce admin-only visibility using the current user's role from the
 *      auth context (do not rely on this UI gate alone — the API enforces it).
 */

import React, { useState } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ProjectStatus = "poc" | "pilot" | "production";

const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  poc: "POC",
  pilot: "Pilot",
  production: "Production",
};

export interface PostModerationProps {
  /** ID of the post being moderated. */
  postId: string;
  /** Current pinned state of the post. */
  isPinned: boolean;
  /** The post's type — the status control only renders for "project". */
  postType?: string;
  /** Current lifecycle status (project posts only); null/undefined elsewhere. */
  status?: ProjectStatus | null;
  /** Called after a successful pin/unpin API call with the new pinned state. */
  onPinChange?: (postId: string, pinned: boolean) => void;
  /** Called after a successful status change. */
  onStatusChange?: (postId: string, status: ProjectStatus) => void;
  /** Called after a successful edit with the updated fields. */
  onEdit?: (postId: string, updates: PostEditUpdates) => void;
  /** Called after a successful remove action. */
  onRemove?: (postId: string) => void;
}

/** Fields that an admin can edit on a post. */
export interface PostEditUpdates {
  title?: string;
  excerpt?: string;
  tags?: string;
  external_url?: string;
  division?: string;
}

// ---------------------------------------------------------------------------
// API helpers  [STUB] — replace with real API client from src/lib/api-types.ts
// ---------------------------------------------------------------------------

const API_BASE = "/api/v1"; // [STUB]

async function apiPinPost(postId: string, pinned: boolean): Promise<void> {
  // [STUB] replace with generated API client once api-types.ts is regenerated
  const resp = await fetch(`${API_BASE}/admin/posts/${postId}/pin`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ pinned }),
  });
  if (!resp.ok) {
    const detail = await resp.json().catch(() => ({}));
    throw new Error(detail?.detail ?? `Failed to ${pinned ? "pin" : "unpin"} post.`);
  }
}

async function apiSetStatus(postId: string, status: ProjectStatus): Promise<void> {
  // Reuses the existing admin edit endpoint (status is a whitelisted
  // editable field — see api.services.moderation._EDITABLE_FIELDS).
  const resp = await fetch(`${API_BASE}/admin/posts/${postId}/edit`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ status }),
  });
  if (!resp.ok) {
    const detail = await resp.json().catch(() => ({}));
    throw new Error(detail?.detail ?? "Failed to update status.");
  }
}

async function apiRemovePost(postId: string): Promise<void> {
  // [STUB] replace with generated API client once api-types.ts is regenerated
  const resp = await fetch(`${API_BASE}/admin/posts/${postId}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!resp.ok) {
    const detail = await resp.json().catch(() => ({}));
    throw new Error(detail?.detail ?? "Failed to remove post.");
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * PostModeration renders the admin action tray (pin, edit, remove) for a post.
 *
 * [STUB] Visual styling is a placeholder; the real design must match the
 * CitiDev Hub admin affordances pixel-for-pixel (see design/project/CitiDev Hub.html).
 */
export function PostModeration({
  postId,
  isPinned,
  postType,
  status,
  onPinChange,
  onStatusChange,
  onEdit,
  onRemove,
}: PostModerationProps): React.ReactElement {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePin = async () => {
    setBusy(true);
    setError(null);
    try {
      await apiPinPost(postId, !isPinned);
      onPinChange?.(postId, !isPinned);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error.");
    } finally {
      setBusy(false);
    }
  };

  const handleStatusChange = async (next: ProjectStatus) => {
    setBusy(true);
    setError(null);
    try {
      await apiSetStatus(postId, next);
      onStatusChange?.(postId, next);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error.");
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm("Remove this post? This action cannot be undone.")) return; // [STUB] replace with design-system confirm dialog
    setBusy(true);
    setError(null);
    try {
      await apiRemovePost(postId);
      onRemove?.(postId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error.");
    } finally {
      setBusy(false);
    }
  };

  // [STUB] Edit flow — placeholder; a real inline-edit form or modal should go here.
  const handleEdit = () => {
    // [STUB] Open edit modal / inline form — not yet implemented.
    console.warn("[STUB] PostModeration edit flow not yet implemented for post:", postId); // [STUB]
  };

  return (
    // [STUB] className tokens are placeholders — replace with design-system classes.
    <div className="post-moderation-tray" role="toolbar" aria-label="Admin post actions">
      {error && (
        <p className="post-moderation-error" role="alert">
          {error}
        </p>
      )}

      <button
        type="button"
        className={`post-moderation-btn post-moderation-btn--pin${isPinned ? " is-pinned" : ""}`}
        aria-label={isPinned ? "Unpin post" : "Pin post"}
        aria-pressed={isPinned}
        disabled={busy}
        onClick={handlePin}
      >
        {/* [STUB] Replace with design-system PinIcon */}
        {isPinned ? "Unpin" : "Pin"}
      </button>

      {postType === "project" && (
        <select
          className="post-moderation-status"
          aria-label="Project status"
          disabled={busy}
          value={status ?? "poc"}
          onChange={(e) => handleStatusChange(e.target.value as ProjectStatus)}
        >
          {Object.entries(PROJECT_STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      )}

      {onEdit && (
        <button
          type="button"
          className="post-moderation-btn post-moderation-btn--edit"
          aria-label="Edit post"
          disabled={busy}
          onClick={handleEdit}
        >
          {/* [STUB] Replace with design-system EditIcon */}
          Edit
        </button>
      )}

      <button
        type="button"
        className="post-moderation-btn post-moderation-btn--remove"
        aria-label="Remove post"
        disabled={busy}
        onClick={handleRemove}
      >
        {/* [STUB] Replace with design-system TrashIcon */}
        Remove
      </button>
    </div>
  );
}

export default PostModeration;
